import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import {
  Employee,
  MessageType,
  MessageThread,
  PersonaExpanded,
  User,
  CompanyDocument,
  EmployeeRecord,
  EmployeeProfile,
  AgentResponseMessage,
  EmployeeReferenceType,
} from "winslow-core";

import {
  CanvasMessage,
  CanvasModeState,
} from "../../../components/canvas-mode/types";

export type EmployeeProfileSidebarState = {
  isOpen: boolean;
  employeeId: string | null;
  employeeData: EmployeeProfile | null;
  isLoading: boolean;
  type: EmployeeReferenceType | null;
};

export type RecentSelection = {
  value: string;
  label: string;
  imageUrl?: string;
  isPersona: boolean;
  timestamp: number;
  workspaceId: string;
};

export type DocumentPreviewModalState = {
  isOpen: boolean;
  document: CompanyDocument | null;
};

export type AgentState = {
  messages: AgentResponseMessage[];
  employees: Employee[];
  personas: PersonaExpanded[];
  currentThread: MessageThread | null;
  threads: MessageThread[];
  prompt: string;
  hasMoreMessages: boolean;
  hasMoreMessagesUp: boolean;
  hasMoreMessagesDown: boolean;
  isLoadingMore: boolean;
  isLoadingMoreUp: boolean;
  isLoadingMoreDown: boolean;
  isInitialLoading: boolean;
  totalMessageCount: number;
  targetMessageId: number | null;
  employeeRecords: EmployeeRecord[];
  companyDocuments: CompanyDocument[];
  workspaceUsers: User[];
  canvasMode: CanvasModeState;
  employeeProfileSidebar: EmployeeProfileSidebarState;
  documentPreviewModal: DocumentPreviewModalState;
  recentSelections: RecentSelection[];
};

// Load recent selections from localStorage - filtered by workspaceId
const loadRecentSelections = (workspaceId?: string): RecentSelection[] => {
  try {
    const stored = localStorage.getItem("recentPersonaEmployeeSelections");
    const parsed = stored ? JSON.parse(stored) : [];
    // console.log("[RecentSelections] Loaded from localStorage:", parsed);

    // Filter by workspaceId if provided
    if (workspaceId) {
      const filtered = parsed.filter(
        (s: RecentSelection) => s.workspaceId === workspaceId
      );
      // console.log("[RecentSelections] Filtered by workspace:", filtered);
      return filtered;
    }

    return parsed;
  } catch (error) {
    console.error("[RecentSelections] Error loading from localStorage:", error);
    return [];
  }
};

//state to be used by other link preview components
const initialState = {
  messages: [],
  employees: [],
  personas: [],
  currentThread: null,
  threads: [],
  prompt: "",
  hasMoreMessages: true,
  hasMoreMessagesUp: true,
  hasMoreMessagesDown: true,
  isLoadingMore: false,
  isLoadingMoreUp: false,
  isLoadingMoreDown: false,
  isInitialLoading: true,
  totalMessageCount: 0,
  targetMessageId: null,
  employeeRecords: [],
  companyDocuments: [],
  workspaceUsers: [],
  canvasMode: {
    isOpen: false,
    message: null,
    ui: {
      sidebar: {
        width: 400,
        isCollapsed: false,
      },
      showUnsavedChangesModal: false,
      isExporting: false,
      isSavingToResources: false,
      isSavingToDocuments: false,
      isGeneratingTitle: false,
    },
  },
  employeeProfileSidebar: {
    isOpen: false,
    employeeId: null,
    employeeData: null,
    isLoading: false,
    type: null,
  },
  documentPreviewModal: {
    isOpen: false,
    document: null,
  },
  recentSelections: loadRecentSelections(),
} as AgentState;

export const agentSlice = createSlice({
  name: "agent",
  initialState,
  reducers: {
    setPrompt: (state: AgentState, action: PayloadAction<string>) => {
      state.prompt = action.payload;
    },
    setMessages: (
      state: AgentState,
      action: PayloadAction<AgentResponseMessage[]>
    ) => {
      console.log("setMessages", action.payload);
      state.messages = action.payload;
      state.isInitialLoading = false; // Set to false when messages are loaded
      if (action.payload.length > 0) {
        const threads = action.payload.map((message) => message.thread);
        const uniqueThreads = threads.filter(
          (thread, index, self) =>
            index === self.findIndex((t) => t?.id === thread?.id)
        );
        state.threads = uniqueThreads as MessageThread[];
        state.currentThread = uniqueThreads[
          uniqueThreads.length - 1
        ] as MessageThread; //newest thread
      }
    },
    updateSpecificMessage: (
      state: AgentState,
      action: PayloadAction<AgentResponseMessage>
    ) => {
      //find the message to update
      const index = state.messages.findIndex(
        (message) => message.id === action.payload.id
      );

      if (index !== -1) {
        state.messages[index] = action.payload;
        return;
      }

      if (state.messages.length > 0) {
        //there is probably a new message on the end of the array
        const lastMessageIndex = state.messages.length - 1;

        if (state.messages[lastMessageIndex].id === -1) {
          //this is the temp human message - replace it
          state.messages[lastMessageIndex] = action.payload;
          return;
        }
      }

      //if we get here, the message is not found - add it to the end of the array
      state.messages = [...state.messages, action.payload];
      return;
    },
    addMessage: (
      state: AgentState,
      action: PayloadAction<AgentResponseMessage>
    ) => {
      state.messages = [...state.messages, action.payload];
      if (action.payload.type === MessageType.NEW_THREAD) {
        state.currentThread = action.payload.thread;
        state.threads = [...state.threads, action.payload.thread];
      }
    },
    prependMessages: (
      state: AgentState,
      action: PayloadAction<{
        messages: AgentResponseMessage[];
        hasMore: boolean;
        totalCount: number;
      }>
    ) => {
      // Prepend older messages to the beginning of the array
      const existingMessageIds = new Set(state.messages.map((m) => m.id));
      const newMessages = action.payload.messages.filter(
        (m) => !existingMessageIds.has(m.id)
      );
      state.messages = [...newMessages, ...state.messages];
      state.hasMoreMessages = action.payload.hasMore;
      state.totalMessageCount = action.payload.totalCount;
      state.isLoadingMore = false;
    },
    setLoadingMore: (state: AgentState, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },
    setInitialLoading: (state: AgentState, action: PayloadAction<boolean>) => {
      state.isInitialLoading = action.payload;
    },
    setPaginationData: (
      state: AgentState,
      action: PayloadAction<{
        hasMore: boolean;
        totalCount: number;
        hasMoreUp?: boolean;
        hasMoreDown?: boolean;
      }>
    ) => {
      state.hasMoreMessages = action.payload.hasMore;
      state.totalMessageCount = action.payload.totalCount;
      // Update bidirectional flags if provided
      if (action.payload.hasMoreUp !== undefined) {
        state.hasMoreMessagesUp = action.payload.hasMoreUp;
      }
      if (action.payload.hasMoreDown !== undefined) {
        state.hasMoreMessagesDown = action.payload.hasMoreDown;
      }
    },
    setTargetMessageId: (
      state: AgentState,
      action: PayloadAction<number | null>
    ) => {
      state.targetMessageId = action.payload;
    },
    setLoadingMoreUp: (state: AgentState, action: PayloadAction<boolean>) => {
      state.isLoadingMoreUp = action.payload;
    },
    setLoadingMoreDown: (state: AgentState, action: PayloadAction<boolean>) => {
      state.isLoadingMoreDown = action.payload;
    },
    appendMessages: (
      state: AgentState,
      action: PayloadAction<{
        messages: AgentResponseMessage[];
        hasMore: boolean;
      }>
    ) => {
      // Append newer messages to the end of the array
      const existingMessageIds = new Set(state.messages.map((m) => m.id));
      const newMessages = action.payload.messages.filter(
        (m) => !existingMessageIds.has(m.id)
      );
      state.messages = [...state.messages, ...newMessages];
      state.hasMoreMessagesDown = action.payload.hasMore;
      state.isLoadingMoreDown = false;
    },
    loadMessagesAroundTarget: (
      state: AgentState,
      action: PayloadAction<{
        messages: AgentResponseMessage[];
        hasMoreUp: boolean;
        hasMoreDown: boolean;
        totalCount: number;
        targetMessageId: number;
      }>
    ) => {
      state.messages = action.payload.messages;
      state.hasMoreMessagesUp = action.payload.hasMoreUp;
      state.hasMoreMessagesDown = action.payload.hasMoreDown;
      state.hasMoreMessages = action.payload.hasMoreDown; // For backward compatibility
      state.totalMessageCount = action.payload.totalCount;
      state.targetMessageId = action.payload.targetMessageId;
      state.isInitialLoading = false;
    },
    setEmployees: (state: AgentState, action: PayloadAction<Employee[]>) => {
      state.employees = action.payload;
    },
    setPersonas: (
      state: AgentState,
      action: PayloadAction<PersonaExpanded[]>
    ) => {
      state.personas = action.payload;
    },
    setCurrentThread: (
      state: AgentState,
      action: PayloadAction<MessageThread | null>
    ) => {
      state.currentThread = action.payload;
    },
    setThreads: (state: AgentState, action: PayloadAction<MessageThread[]>) => {
      state.threads = action.payload;
    },
    setEmployeeRecords: (
      state: AgentState,
      action: PayloadAction<EmployeeRecord[]>
    ) => {
      state.employeeRecords = action.payload;
    },
    setCompanyDocuments: (
      state: AgentState,
      action: PayloadAction<CompanyDocument[]>
    ) => {
      state.companyDocuments = action.payload;
    },
    setWorkspaceUsers: (state: AgentState, action: PayloadAction<User[]>) => {
      state.workspaceUsers = action.payload;
    },
    // Canvas Mode actions
    openCanvasMode: (
      state: AgentState,
      action: PayloadAction<{
        content: string;
        messageId: number;
        title?: string;
        linkRecordId?: string | null;
      }>
    ) => {
      // Ensure canvasMode exists (defensive programming)
      if (!state.canvasMode) {
        state.canvasMode = {
          isOpen: false,
          message: null,
          ui: {
            sidebar: {
              width: 400,
              isCollapsed: false,
            },
            showUnsavedChangesModal: false,
            isExporting: false,
            isSavingToResources: false,
            isSavingToDocuments: false,
            isGeneratingTitle: false,
          },
        };
      }

      state.canvasMode.isOpen = true;
      state.canvasMode.message = {
        id: action.payload.messageId,
        type: MessageType.AI_CANVAS_MESSAGE,
        message: {
          title: action.payload.title || "",
          content: action.payload.content,
        },
        linkRecordId: action.payload.linkRecordId ?? null,
        hasChanges: false,
        isAutoSaving: false,
      };
    },
    setCanvasMessage: (
      state: AgentState,
      action: PayloadAction<CanvasMessage>
    ) => {
      state.canvasMode.message = action.payload;
    },
    closeCanvasMode: (state: AgentState) => {
      // Ensure canvasMode exists (defensive programming)
      if (!state.canvasMode) return;

      state.canvasMode.isOpen = false;
      state.canvasMode.message = null;
      state.canvasMode.ui.showUnsavedChangesModal = false;
    },
    updateCanvasContent: (state: AgentState, action: PayloadAction<string>) => {
      if (state.canvasMode?.message) {
        state.canvasMode.message.message.content = action.payload;
        state.canvasMode.message.hasChanges = true;
      }
    },
    updateCanvasTitle: (state: AgentState, action: PayloadAction<string>) => {
      if (state.canvasMode?.message) {
        state.canvasMode.message.message.title = action.payload;
        state.canvasMode.message.hasChanges = true;
      }
    },
    setCanvasAutoSaving: (
      state: AgentState,
      action: PayloadAction<boolean>
    ) => {
      if (state.canvasMode?.message) {
        state.canvasMode.message.isAutoSaving = action.payload;
      }
    },
    setCanvasMessageSaved: (state: AgentState) => {
      if (state.canvasMode?.message) {
        state.canvasMode.message.hasChanges = false;
        state.canvasMode.message.lastSaved = new Date().toISOString();
      }
    },
    showUnsavedChangesModal: (state: AgentState) => {
      if (state.canvasMode?.ui) {
        state.canvasMode.ui.showUnsavedChangesModal = true;
      }
    },
    hideUnsavedChangesModal: (state: AgentState) => {
      if (state.canvasMode?.ui) {
        state.canvasMode.ui.showUnsavedChangesModal = false;
      }
    },
    setCanvasExporting: (state: AgentState, action: PayloadAction<boolean>) => {
      if (state.canvasMode?.ui) {
        state.canvasMode.ui.isExporting = action.payload;
      }
    },
    setCanvasSavingToResources: (
      state: AgentState,
      action: PayloadAction<boolean>
    ) => {
      if (state.canvasMode?.ui) {
        state.canvasMode.ui.isSavingToResources = action.payload;
      }
    },
    setCanvasSavingToDocuments: (
      state: AgentState,
      action: PayloadAction<boolean>
    ) => {
      if (state.canvasMode?.ui) {
        state.canvasMode.ui.isSavingToDocuments = action.payload;
      }
    },
    setCanvasGeneratingTitle: (
      state: AgentState,
      action: PayloadAction<boolean>
    ) => {
      if (state.canvasMode?.ui) {
        state.canvasMode.ui.isGeneratingTitle = action.payload;
      }
    },
    setCanvasSidebarWidth: (
      state: AgentState,
      action: PayloadAction<number>
    ) => {
      if (state.canvasMode?.ui?.sidebar) {
        state.canvasMode.ui.sidebar.width = action.payload;
      }
    },
    // Employee Profile Sidebar actions
    openEmployeeProfileSidebar: (
      state: AgentState,
      action: PayloadAction<{
        employeeId: string;
        employeeData?: EmployeeProfile | null;
        type?: EmployeeReferenceType;
      }>
    ) => {
      // Ensure employeeProfileSidebar exists
      if (!state.employeeProfileSidebar) {
        state.employeeProfileSidebar = {
          isOpen: false,
          employeeId: null,
          employeeData: null,
          isLoading: false,
          type: null,
        };
      }

      // Safety check: if the same employee is already open, do nothing
      const targetType = action.payload.type || EmployeeReferenceType.EMPLOYEE;
      if (
        state.employeeProfileSidebar.isOpen &&
        state.employeeProfileSidebar.employeeId === action.payload.employeeId &&
        state.employeeProfileSidebar.type === targetType
      ) {
        return;
      }

      state.employeeProfileSidebar.isOpen = true;
      state.employeeProfileSidebar.employeeId = action.payload.employeeId;
      state.employeeProfileSidebar.type = targetType;

      const hasExistingData =
        state.employeeProfileSidebar.employeeData !== null;

      if (action.payload.employeeData) {
        state.employeeProfileSidebar.employeeData = action.payload.employeeData;
        state.employeeProfileSidebar.isLoading = false;
      } else if (hasExistingData) {
        // If we already have data for this employee, don't set loading to true
        state.employeeProfileSidebar.isLoading = false;
      } else {
        // Only set loading to true if we don't have existing data
        state.employeeProfileSidebar.isLoading = true;
      }
    },
    closeEmployeeProfileSidebar: (state: AgentState) => {
      // Ensure employeeProfileSidebar exists
      if (!state.employeeProfileSidebar) {
        state.employeeProfileSidebar = {
          isOpen: false,
          employeeId: null,
          employeeData: null,
          isLoading: false,
          type: null,
        };
        return;
      }

      state.employeeProfileSidebar.isOpen = false;
      state.employeeProfileSidebar.employeeId = null;
      state.employeeProfileSidebar.employeeData = null;
      state.employeeProfileSidebar.isLoading = false;
      state.employeeProfileSidebar.type = null;
    },
    setEmployeeProfileSidebarData: (
      state: AgentState,
      action: PayloadAction<EmployeeProfile>
    ) => {
      // Ensure employeeProfileSidebar exists
      if (!state.employeeProfileSidebar) {
        state.employeeProfileSidebar = {
          isOpen: false,
          employeeId: null,
          employeeData: null,
          isLoading: false,
          type: null,
        };
      }

      state.employeeProfileSidebar.employeeData = action.payload;
      state.employeeProfileSidebar.isLoading = false;
    },
    setEmployeeProfileSidebarLoading: (
      state: AgentState,
      action: PayloadAction<boolean>
    ) => {
      // Ensure employeeProfileSidebar exists
      if (!state.employeeProfileSidebar) {
        state.employeeProfileSidebar = {
          isOpen: false,
          employeeId: null,
          employeeData: null,
          isLoading: false,
          type: null,
        };
      }

      state.employeeProfileSidebar.isLoading = action.payload;
    },
    // Document Preview Modal actions
    openDocumentPreviewModal: (
      state: AgentState,
      action: PayloadAction<{ document: CompanyDocument }>
    ) => {
      // Ensure documentPreviewModal exists
      if (!state.documentPreviewModal) {
        state.documentPreviewModal = {
          isOpen: false,
          document: null,
        };
      }

      state.documentPreviewModal.isOpen = true;
      state.documentPreviewModal.document = action.payload.document;
    },
    closeDocumentPreviewModal: (state: AgentState) => {
      // Ensure documentPreviewModal exists
      if (!state.documentPreviewModal) {
        state.documentPreviewModal = {
          isOpen: false,
          document: null,
        };
        return;
      }

      state.documentPreviewModal.isOpen = false;
      state.documentPreviewModal.document = null;
    },
    // Recent Selections actions
    addRecentSelection: (
      state: AgentState,
      action: PayloadAction<Omit<RecentSelection, "timestamp">>
    ) => {
      // Ensure recentSelections exists (defensive programming)
      if (!state.recentSelections) {
        state.recentSelections = [];
      }

      const newSelection = {
        ...action.payload,
        timestamp: Date.now(),
      };

      // console.log("[RecentSelections] Adding new selection:", newSelection);
      // console.log(
      //   "[RecentSelections] Current selections before add:",
      //   state.recentSelections
      // );

      // Load ALL selections from localStorage (all workspaces)
      const allSelections = loadRecentSelections();

      // Remove if already exists in this workspace (to avoid duplicates)
      const filteredAll = allSelections.filter(
        (s) =>
          !(
            s.value === newSelection.value &&
            s.workspaceId === newSelection.workspaceId
          )
      );

      // Add new selection to beginning
      const updatedAll = [newSelection, ...filteredAll];

      // Keep only last 5 per workspace
      const workspaceSelections = updatedAll
        .filter((s) => s.workspaceId === newSelection.workspaceId)
        .slice(0, 5);
      const otherWorkspaceSelections = updatedAll.filter(
        (s) => s.workspaceId !== newSelection.workspaceId
      );
      const finalAll = [...workspaceSelections, ...otherWorkspaceSelections];

      // Update state with only current workspace selections
      state.recentSelections = workspaceSelections;

      console.log(
        "[RecentSelections] Updated selections for workspace:",
        state.recentSelections
      );

      // Save ALL workspaces to localStorage
      try {
        localStorage.setItem(
          "recentPersonaEmployeeSelections",
          JSON.stringify(finalAll)
        );
        // console.log("[RecentSelections] Saved to localStorage");
      } catch (error) {
        console.error(
          "Failed to save recent selections to localStorage:",
          error
        );
      }
    },
    loadRecentSelectionsForWorkspace: (
      state: AgentState,
      action: PayloadAction<string>
    ) => {
      const workspaceId = action.payload;
      state.recentSelections = loadRecentSelections(workspaceId);
      console.log(
        "[RecentSelections] Loaded for workspace:",
        workspaceId,
        state.recentSelections
      );
    },
    clearRecentSelections: (state: AgentState) => {
      state.recentSelections = [];

      try {
        localStorage.removeItem("recentPersonaEmployeeSelections");
      } catch (error) {
        console.error(
          "Failed to clear recent selections from localStorage:",
          error
        );
      }
    },
  },
});

export const {
  setMessages,
  setEmployees,
  setPersonas,
  addMessage,
  setCurrentThread,
  setPrompt,
  prependMessages,
  appendMessages,
  loadMessagesAroundTarget,
  setLoadingMore,
  setLoadingMoreUp,
  setLoadingMoreDown,
  setInitialLoading,
  setPaginationData,
  setTargetMessageId,
  setEmployeeRecords,
  setCompanyDocuments,
  setWorkspaceUsers,
  // Canvas Mode actions
  openCanvasMode,
  closeCanvasMode,
  updateCanvasContent,
  updateCanvasTitle,
  setCanvasMessage,
  setCanvasAutoSaving,
  setCanvasMessageSaved,
  showUnsavedChangesModal,
  hideUnsavedChangesModal,
  setCanvasExporting,
  setCanvasSavingToResources,
  setCanvasSavingToDocuments,
  setCanvasGeneratingTitle,
  setCanvasSidebarWidth,
  // Employee Profile Sidebar actions
  openEmployeeProfileSidebar,
  closeEmployeeProfileSidebar,
  setEmployeeProfileSidebarData,
  setEmployeeProfileSidebarLoading,
  // Document Preview Modal actions
  openDocumentPreviewModal,
  closeDocumentPreviewModal,
  updateSpecificMessage,
  // Recent Selections actions
  addRecentSelection,
  loadRecentSelectionsForWorkspace,
  clearRecentSelections,
} = agentSlice.actions;

export const selectMessages = (state: RootState) => state.agent.messages;
export const selectEmployees = (state: RootState) => state.agent.employees;
export const selectPersonas = (state: RootState) => state.agent.personas;
export const selectCurrentThread = (state: RootState) =>
  state.agent.currentThread;
export const selectThreads = (state: RootState) => state.agent.threads;
export const selectPrompt = (state: RootState) => state.agent.prompt;
export const selectHasMoreMessages = (state: RootState) =>
  state.agent.hasMoreMessages;
export const selectRecentSelections = (state: RootState) =>
  state.agent.recentSelections || [];
export const selectHasMoreMessagesUp = (state: RootState) =>
  state.agent.hasMoreMessagesUp;
export const selectHasMoreMessagesDown = (state: RootState) =>
  state.agent.hasMoreMessagesDown;
export const selectIsLoadingMore = (state: RootState) =>
  state.agent.isLoadingMore;
export const selectIsLoadingMoreUp = (state: RootState) =>
  state.agent.isLoadingMoreUp;
export const selectIsLoadingMoreDown = (state: RootState) =>
  state.agent.isLoadingMoreDown;
export const selectIsInitialLoading = (state: RootState) =>
  state.agent.isInitialLoading;
export const selectTargetMessageId = (state: RootState) =>
  state.agent.targetMessageId;
export const selectTotalMessageCount = (state: RootState) =>
  state.agent.totalMessageCount;
export const selectEmployeeRecords = (state: RootState) =>
  state.agent.employeeRecords;
export const selectCompanyDocuments = (state: RootState) =>
  state.agent.companyDocuments;
export const selectWorkspaceUsers = (state: RootState) =>
  state.agent.workspaceUsers;
export const selectCanvasMessage = (state: RootState) =>
  state.agent.canvasMode?.message ?? null;

// Canvas Mode selectors
export const selectCanvasMode = (state: RootState) => state.agent.canvasMode;
export const selectCanvasModeIsOpen = (state: RootState) =>
  state.agent.canvasMode?.isOpen ?? false;
export const selectCanvasModeMessage = (state: RootState) =>
  state.agent.canvasMode?.message ?? null;
export const selectCanvasModeUI = (state: RootState) =>
  state.agent.canvasMode?.ui ?? {
    sidebar: { width: 400, isCollapsed: false },
    showUnsavedChangesModal: false,
    isExporting: false,
    isSavingToResources: false,
    isSavingToDocuments: false,
    isGeneratingTitle: false,
  };
export const selectCanvasModeHasUnsavedChanges = (state: RootState) =>
  state.agent.canvasMode?.message?.hasChanges ?? false;

// Employee Profile Sidebar selectors
export const selectEmployeeProfileSidebar = (state: RootState) =>
  state.agent.employeeProfileSidebar;
export const selectEmployeeProfileSidebarIsOpen = (state: RootState) =>
  state.agent.employeeProfileSidebar?.isOpen ?? false;
export const selectEmployeeProfileSidebarEmployeeId = (state: RootState) =>
  state.agent.employeeProfileSidebar?.employeeId ?? null;
export const selectEmployeeProfileSidebarData = (state: RootState) =>
  state.agent.employeeProfileSidebar?.employeeData ?? null;
export const selectEmployeeProfileSidebarIsLoading = (state: RootState) =>
  state.agent.employeeProfileSidebar?.isLoading ?? false;
export const selectEmployeeProfileSidebarType = (state: RootState) =>
  state.agent.employeeProfileSidebar?.type ?? null;

// Document Preview Modal selectors
export const selectDocumentPreviewModalIsOpen = (state: RootState) =>
  state.agent.documentPreviewModal?.isOpen ?? false;
export const selectDocumentPreviewModalDocument = (state: RootState) =>
  state.agent.documentPreviewModal?.document ?? null;

export default agentSlice.reducer;
